<template>

  <v-container fluid class="px-0 route-info-overview">

    <!-------------------->
    <!-- Route Updating -->
    <!-------------------->
    <template v-if="isDataUpdating">
      <v-row>
        <v-col class="d-flex align-center justify-center" style="height:120px">
          <v-progress-circular indeterminate :size="50" color="primary" />
        </v-col>
      </v-row>
      <v-row class="mt-2">
        <v-col align="center">
          <div style="font-size:1.04rem;color:#a2a2a2">
            The route is being updated.
          </div>
          <div style="font-size:1.04rem;color:#a2a2a2">
            (This may take a few minutes)
          </div>
          <div style="font-size:1.1rem;color:#d2d2d2" class="mt-6">
            Do not refresh the page until the processing has completed.
          </div>
        </v-col>
      </v-row>
    </template>

    <!------------------------------------------>
    <!-- Display Route Details and Order List -->
    <!------------------------------------------>
    <template v-else>
      <!-- Data updating error -->
      <v-container class="mb-6" v-if="dataUpdatingError">
        <v-row align="center" justify="center">
          <v-col cols="12" xs="12" sm="12" md="12" lg="9" class="d-flex align-center justify-center error-container">
            <v-icon large color="#B71C1C" class="mr-3">mdi-alert-circle-outline</v-icon>
            <div class="data-updating-error-text">{{ dataUpdatingError }}</div>
            <v-btn large class="ml-8" @click="clearError">
              Close
            </v-btn>
          </v-col>
        </v-row>
      </v-container>

      <!-- Route Details -->
      <route-details
          class="page-content"
          :route="route"
          @updated="$parent.loadItem()" />

      <!-- Route Orders -->
      <v-container fluid class="mt-5 ma-0 px-8 pb-8 page-content">
        <v-row>
          <v-col>
            <div class="title">Containing Orders ({{ orders.length }})</div>
          </v-col>
        </v-row>
        <v-row class="px-3" v-for="order in sortedRouteOrders" :key="`order-${order.order_number}`">
          <v-col class="py-0">
            <route-order-action-card
                class="mb-0"
                style="max-width:800px;"
                :order="order"
                :isUpdating="isDataUpdating"
                :hideActions="!canEdit"
                @rescheduleOrder="rescheduleOrder" />
          </v-col>
        </v-row>
      </v-container>
    </template>

  </v-container>

</template>

<script>
import RouteDetails from '@/components/routes/RouteDetails'
import RouteOrderActionCard from '@/components/routes/RouteOrderActionCard'

export default {
  name: 'RouteInfoOverview',

  components: {
    RouteDetails,
    RouteOrderActionCard
  },

  props: {
    routeId: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      isDataUpdating: false,
      dataUpdatingError: null
    }
  },

  computed: {
    route () {
      return this.$parent.route
    },
    // The proper sort for the dropoffs (per the built route_info.steps)
    sortForDropoffs () {
      const routeSteps = this.route.route_info.steps || []
      return routeSteps
        .filter(step => step.type === 'dropoff' && Array.isArray(step.order_ids))
        .reduce((acc, step) => acc.concat(step.order_ids), [])
    },
    orders () {
      return (this.route.orders)
        ? this.$root.utils.orderBy(this.route.orders, ['estimated_receiver_arrival_time'], ['asc'])
        : []
    },
    sortedRouteOrders () {
      const undeliverableOrders = this.orders.filter(order => (order.order_status === 'RESCHEDULED' || order.order_status === 'CANCELED'))
      const deliverbleOrders = this.orders.filter(order => this.sortForDropoffs.includes(order.id))
        .sort((a, b) => this.sortForDropoffs.indexOf(a.id) - this.sortForDropoffs.indexOf(b.id))
      return [...undeliverableOrders, ...deliverbleOrders]
    },
    canEdit () {
      return this.$parent.canEdit
    }
  },

  methods: {
    // Clear errors
    clearError () {
      this.dataUpdatingError = null
    },

    // Handle reschedule of order within a route
    // data.order = the order to reschedule
    // data.notes = the dispatcher notes (optional)
    async rescheduleOrder (data = {}) {
      const orderToReschedule = data.order
      const notes = data.notes
      if (!orderToReschedule) return false

      this.isDataUpdating = true

      try {
        const bodyData = { order_ids: [orderToReschedule.id] }
        if (notes) bodyData.dispatcher_notes = notes

        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `route/${this.routeId}/reschedule-orders`,
          opts: {
            bodyData
          }
        })

        // Reload route and refresh UI
        await this.$parent.loadItem()
      } catch (err) {
        if (this.$root.utils.isEmpty(err)) this.dataUpdatingError = 'The order could not be rescheduled.'
        else this.dataUpdatingError = err.message || err.text || err
      } finally {
        this.isDataUpdating = false
      }
    }
  }
}
</script>

<style>
  .route-info-overview .error-container {
    border: 1px solid #CC0000;
    border-radius: 4px;
  }

  .route-info-overview .data-updating-error-text {
    color: #B71C1C;
    font-size: 1.07rem;
  }
</style>
